import { auditLogsApi } from "app/services/api/auditLogsApi";
import { authorisationApi } from "app/services/api/authorisationApi";
import { adminAssessmentsApi } from "app/services/api/adminAssessmentsApi";
import { categoriesApi } from "app/services/api/categoriesApi";
import { countriesApi } from "app/services/api/countriesApi";
import { residentsApi } from "app/services/api/residentsApi";
import { usersApi } from "app/services/api/usersApi";
import { rolesApi } from "app/services/api/rolesApi";
import { locationsApi } from "app/services/api/locationsApi";
import { medicationsApi } from "app/services/api/medicationsApi";
import { formsApi } from "app/services/api/formsApi";
import { staticFormsApi } from "app/services/api/staticFormsApi";
import { calendarApi } from "app/services/api/calendarApi";
import { shiftHandoverApi } from "app/services/api/shiftHandoverApi";
import { offlineApi } from "app/services/api/offlineApi";
import { statusFlagsApi } from "app/services/api/statusFlagsApi";
import { reportsApi } from "app/services/api/reportsApi";
import { store } from "app/services/store";

/**
 * Shared utilities for throttling prefetch requests based on active API load
 */

const API_SLICES = [
  auditLogsApi.reducerPath,
  authorisationApi.reducerPath,
  adminAssessmentsApi.reducerPath,
  categoriesApi.reducerPath,
  countriesApi.reducerPath,
  residentsApi.reducerPath,
  usersApi.reducerPath,
  rolesApi.reducerPath,
  locationsApi.reducerPath,
  medicationsApi.reducerPath,
  formsApi.reducerPath,
  staticFormsApi.reducerPath,
  calendarApi.reducerPath,
  shiftHandoverApi.reducerPath,
  offlineApi.reducerPath,
  statusFlagsApi.reducerPath,
  reportsApi.reducerPath,
] as const;

/**
 * Count the number of currently active (pending) RTK Query requests
 */
export function getActiveRequestCount(): number {
  const state = store.getState() as any;
  let activeCount = 0;

  API_SLICES.forEach((apiKey) => {
    const apiState = state[apiKey];
    if (apiState?.queries) {
      Object.values(apiState.queries).forEach((query: any) => {
        if (query?.status === "pending") {
          activeCount++;
        }
      });
    }
  });

  return activeCount;
}

/**
 * Wait until the number of active requests drops below the threshold
 * @param threshold - Maximum number of active requests to allow (default: 2)
 * @param checkIntervalMs - How often to check (default: 500ms)
 */
export async function waitForCapacity(
  threshold: number = 2,
  checkIntervalMs: number = 500,
): Promise<void> {
  return new Promise((resolve) => {
    const checkCapacity = () => {
      const activeCount = getActiveRequestCount();

      if (activeCount <= threshold) {
        resolve();
      } else {
        setTimeout(checkCapacity, checkIntervalMs);
      }
    };
    checkCapacity();
  });
}

/**
 * Add a delay between requests to avoid bursts
 * @param delayMs - Delay in milliseconds (default: 100ms)
 */
export async function delayBetweenRequests(
  delayMs: number = 100,
): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, delayMs));
}
